import {
  renderPackageReviewFilesSection,
} from './package-review-surface';
import {
  renderSkillInstallTargetOptions,
  skillInstallCommandForTarget,
  skillInstallCommandsForSkill,
} from './skill-install-display';
import {
  fullSkillDigest,
  shortSkillDigest,
  skillDigestValue,
} from './skill-digest-display';
import { escapeHtml } from './html';
import { skillCertificationBadge } from './status-ui';
import type {
  SkillDetail,
  SkillCertificationStatusResponse,
  SkillFeedbackResponse,
  SkillFile,
  SkillRelatedIssuesResponse,
} from '../types/api';

type SkillFlywheelSummary = {
  download_count?: number | null;
  feedback_count?: number | null;
  related_issue_count?: number | null;
  active_issue_count?: number | null;
  version?: string | null;
  source_issue_id?: number | null;
  source_issue_title?: string | null;
};

export type SkillDetailPageShellOptions = {
  skill: SkillDetail;
  files: SkillFile[];
  issues: SkillRelatedIssuesResponse | null;
  feedback: SkillFeedbackResponse | null;
  certification?: SkillCertificationStatusResponse | null;
  currentUserId?: number | null;
  currentFileIndex: number;
  renderRelatedIssuesSection: (issues: SkillRelatedIssuesResponse | null) => string;
  renderFlywheelSummary: (summary: SkillFlywheelSummary) => string;
};

function renderCertificationRequestPanel(
  skill: SkillDetail,
  certification: SkillCertificationStatusResponse | null,
  currentUserId: number | null,
): string {
  if (!certification || skill.certification_level === 'certified') return '';
  const request = certification.latest_request;
  const isOwner = skill.governance?.owner_type === 'user'
    && skill.governance.owner_id === String(currentUserId ?? '');
  const active = request?.release_is_current
    && (request.status === 'pending' || request.status === 'in_review');
  const canRequest = isOwner && !active;
  const statusLabel = !request
    ? 'Not requested'
    : request.status === 'changes_requested'
      ? 'Changes requested'
      : request.status.replace('_', ' ');
  const reviewComment = request?.review_comment
    ? '<p class="mt-2 text-xs leading-5 text-zinc-400">' + escapeHtml(request.review_comment) + '</p>'
    : '';
  return '' +
    '<section id="skill-certification-request" class="rounded-lg border border-zinc-800 bg-zinc-950/30 p-4">' +
      '<div class="flex flex-wrap items-start justify-between gap-3">' +
        '<div class="min-w-0">' +
          '<h2 class="text-sm font-semibold text-zinc-100"><i class="fas fa-shield-halved mr-2 text-emerald-400" aria-hidden="true"></i>Certification review</h2>' +
          '<p class="mt-1 text-xs text-zinc-500">' + escapeHtml(statusLabel) +
            (request ? ' for v' + escapeHtml(request.version) + ' · ' + escapeHtml(shortSkillDigest(request)) : '') +
          '</p>' +
          reviewComment +
        '</div>' +
        (canRequest
          ? '<button id="request-certification-btn" type="button" class="btn-ghost text-sm"><i class="fas fa-shield mr-1.5" aria-hidden="true"></i>' +
              (request?.status === 'changes_requested' ? 'Resubmit' : 'Request review') + '</button>'
          : '') +
      '</div>' +
      '<p id="certification-request-status" class="mt-2 text-xs text-zinc-500" aria-live="polite"></p>' +
    '</section>';
}

function stripFrontmatter(content: string): string {
  if (!content || !content.startsWith('---\n')) return content || '';
  const end = content.indexOf('\n---\n', 4);
  if (end === -1) return content;
  return content.slice(end + 5);
}

export function renderSkillMd(content: string): string {
  if (window.ContentRenderer) {
    return window.ContentRenderer.render(content, {
      filename: 'SKILL.md',
      mode: 'markdown-preview',
    });
  }
  const body = stripFrontmatter(content);
  return '<pre class="text-sm text-zinc-300 whitespace-pre-wrap">' + escapeHtml(body) + '</pre>';
}

export function installCommandsForSkill(skill: SkillDetail): NonNullable<SkillDetail['install_commands']> {
  return skillInstallCommandsForSkill({
    skillName: skill.name || 'Unnamed skill',
    baseUrl: window.location.origin,
    installCommands: skill.install_commands,
  });
}

export function installCommandForTarget(skill: SkillDetail, target: string): string {
  return skillInstallCommandForTarget(installCommandsForSkill(skill), target);
}

function absoluteSkillUrl(value: string): string {
  if (/^https?:\/\//i.test(value)) return value;
  const base = window.location.origin.replace(/\/$/, '');
  return value.startsWith('/') ? base + value : base + '/' + value;
}

function directSkillMdUrl(skill: SkillDetail): string {
  const fallback = '/.well-known/skills/' + encodeURIComponent(skill.name || '') + '/SKILL.md';
  return absoluteSkillUrl(skill.well_known_skill_md_absolute_url || skill.well_known_skill_md_url || fallback);
}

function renderCloneDownloadPanel(skill: SkillDetail): string {
  const cloneUrl = skill.git_clone_url || '';
  const cloneCommand = cloneUrl ? 'git clone ' + cloneUrl : '';
  const downloadUrl = absoluteSkillUrl(skill.download_url || ('/api/skills/' + encodeURIComponent(String(skill.id)) + '/download'));
  const skillMdUrl = absoluteSkillUrl(skill.skill_md_url || ('/api/skills/' + encodeURIComponent(String(skill.id)) + '/download?format=md'));
  return '' +
    '<div class="mt-4 rounded-lg border border-zinc-800 bg-zinc-950/50 p-4">' +
      '<div class="flex flex-wrap items-start justify-between gap-3">' +
        '<div>' +
          '<h2 class="text-sm font-semibold text-zinc-200">Clone or download</h2>' +
          '<p class="mt-1 text-xs text-zinc-500">Use these paths when an agent needs the package source instead of an install command.</p>' +
        '</div>' +
        '<div class="flex flex-wrap gap-2">' +
          '<a class="btn-ghost text-sm" href="' + escapeHtml(downloadUrl) + '"><i class="fas fa-file-zipper mr-1.5"></i>ZIP</a>' +
          '<a class="btn-ghost text-sm" href="' + escapeHtml(skillMdUrl) + '"><i class="fas fa-file-lines mr-1.5"></i>SKILL.md</a>' +
        '</div>' +
      '</div>' +
      (cloneCommand
        ? '<div class="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">' +
            '<code id="skill-git-clone-command" class="block flex-1 overflow-x-auto rounded bg-zinc-950 px-3 py-2 text-xs text-zinc-300">' + escapeHtml(cloneCommand) + '</code>' +
            '<button id="skill-git-clone-copy" type="button" class="btn-ghost h-9 w-9 shrink-0 px-0 text-sm" title="Copy git clone command" aria-label="Copy git clone command"><i class="fas fa-copy" aria-hidden="true"></i><span class="sr-only">Copy git clone command</span></button>' +
          '</div>' +
          '<p class="mt-2 text-xs text-zinc-500">For just-published skills, direct SKILL.md and ZIP are the fastest read paths while registry clone metadata catches up.</p>'
        : '<p class="mt-3 text-sm text-amber-300">Registry clone URL is not available yet. Use the SKILL.md or ZIP links above.</p>') +
      '<p id="skill-git-clone-copy-status" class="mt-2 text-xs text-zinc-500" aria-live="polite"></p>' +
    '</div>';
}

function renderDirectUsePanel(skill: SkillDetail): string {
  const url = directSkillMdUrl(skill);
  return '' +
    '<div class="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4">' +
      '<div class="flex flex-wrap items-start justify-between gap-3">' +
        '<div>' +
          '<h3 class="text-sm font-semibold text-emerald-100">Agent-readable SKILL.md</h3>' +
        '</div>' +
        '<a class="btn-ghost text-sm" href="' + escapeHtml(url) + '" target="_blank" rel="noopener noreferrer">Open</a>' +
      '</div>' +
      '<div class="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">' +
        '<code id="skill-direct-use-url" class="block flex-1 overflow-x-auto rounded bg-zinc-950 px-3 py-2 text-xs text-emerald-300">' + escapeHtml(url) + '</code>' +
        '<button id="skill-direct-use-copy" type="button" class="btn-primary h-9 w-9 shrink-0 px-0 text-sm" title="Copy SKILL.md URL" aria-label="Copy agent-readable SKILL.md URL"><i class="fas fa-copy" aria-hidden="true"></i><span class="sr-only">Copy SKILL.md URL</span></button>' +
      '</div>' +
      '<p id="skill-direct-use-copy-status" class="mt-2 text-xs text-zinc-500" aria-live="polite"></p>' +
    '</div>';
}

function renderInstallPanel(skill: SkillDetail): string {
  const skillName = skill.name || 'Unnamed skill';
  const command = installCommandForTarget(skill, 'codex');
  return '' +
    '<div class="rounded-lg border border-brand-500/30 bg-brand-500/10 p-4">' +
      '<div class="flex flex-wrap items-start justify-between gap-3">' +
        '<div>' +
          '<h3 class="text-sm font-semibold text-brand-100">Install this skill</h3>' +
          '<p class="mt-1 text-xs text-zinc-400">Install into your agent framework with the open skills CLI.</p>' +
        '</div>' +
        '<label class="text-xs text-zinc-400">Target ' +
          '<select id="skill-install-target" class="ml-2 rounded bg-zinc-950 border border-zinc-700 px-2 py-1 text-zinc-100">' +
            renderSkillInstallTargetOptions('codex') +
          '</select>' +
        '</label>' +
      '</div>' +
      '<div class="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">' +
        '<code id="skill-install-command" data-skill-name="' + escapeHtml(skillName) + '" class="block flex-1 overflow-x-auto rounded bg-zinc-950 px-3 py-2 text-xs text-green-300">' + escapeHtml(command) + '</code>' +
        '<button id="skill-install-copy" type="button" class="btn-primary h-9 w-9 shrink-0 px-0 text-sm" title="Copy install command" aria-label="Copy install command"><i class="fas fa-copy" aria-hidden="true"></i><span class="sr-only">Copy install command</span></button>' +
      '</div>' +
      '<p class="mt-2 text-xs text-zinc-500">Installs into the selected agent skills directory. The skills CLI will ask for confirmation before making changes. Choose Current directory to install without targeting a specific agent.</p>' +
      '<p id="skill-install-copy-status" class="mt-2 text-xs text-zinc-500" aria-live="polite"></p>' +
    '</div>';
}

function renderUsePanel(skill: SkillDetail): string {
  return '' +
    '<section id="skill-use-panel" class="rounded-lg border border-zinc-800 bg-zinc-950/30 p-4">' +
      '<div class="flex flex-wrap items-start justify-between gap-3">' +
        '<div>' +
          '<h2 class="text-base font-semibold text-zinc-100">Use this skill</h2>' +
          '<p class="mt-1 text-xs text-zinc-500">Start with the install command, or give an agent the direct SKILL.md URL.</p>' +
        '</div>' +
      '</div>' +
      '<div class="mt-4 grid grid-cols-1 gap-4">' +
        renderInstallPanel(skill) +
        renderDirectUsePanel(skill) +
      '</div>' +
      renderCloneDownloadPanel(skill) +
    '</section>';
}

function renderSkillInstructionsPanel(skill: SkillDetail): string {
  return '' +
    '<section id="skill-instructions" class="card p-5" aria-labelledby="skill-instructions-title">' +
      '<div class="mb-4 flex flex-wrap items-start justify-between gap-3">' +
        '<div class="min-w-0">' +
          '<div class="mb-2 inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-2.5 py-1 text-xs font-medium text-brand-200">' +
            '<i class="fas fa-file-lines" aria-hidden="true"></i>' +
            'Published SKILL.md' +
          '</div>' +
          '<h2 id="skill-instructions-title" class="text-lg font-semibold text-white">Skill instructions</h2>' +
          '<p class="mt-1 max-w-2xl text-sm leading-6 text-zinc-400">This is the skill itself: the instructions an agent reads before acting.</p>' +
        '</div>' +
        '<a class="btn-ghost text-sm whitespace-nowrap" href="' + escapeHtml(directSkillMdUrl(skill)) + '" target="_blank" rel="noopener noreferrer">' +
          '<i class="fas fa-arrow-up-right-from-square mr-1.5" aria-hidden="true"></i>Open raw SKILL.md' +
        '</a>' +
      '</div>' +
      (skill.skill_md_content
        ? '<div id="skill-md-content" class="prose-sm max-w-none"></div>'
        : '<div class="rounded border border-zinc-800 bg-zinc-950/50 p-4 text-sm text-zinc-500">SKILL.md content is unavailable. Use the raw SKILL.md or package files below.</div>') +
    '</section>';
}

function subjectLabel(type: string | null | undefined, id: string | null | undefined): string | null {
  if (!type || !id) return null;
  return type + ':' + id;
}

function humanizeToken(value: string | null | undefined): string | null {
  const text = String(value ?? '').trim();
  if (!text) return null;
  return text
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function publicationAuthorityLabel(value: string | null | undefined): string | null {
  if (value === 'requester') return 'Issue Creator authority';
  if (value === 'owner') return 'Skill owner authority';
  if (value === 'official' || value === 'certified') return 'Maintainer authority';
  return humanizeToken(value);
}

function publicationTargetLabel(value: string | null | undefined): string | null {
  if (value === 'new_skill') return 'New skill';
  if (value === 'version_update') return 'Update existing skill';
  if (value === 'fork') return 'Fork / variant';
  return humanizeToken(value);
}

function renderSkillLink(skillId: number | null | undefined, skillName: string | null | undefined): string | null {
  if (!skillId) return null;
  const label = skillName ? escapeHtml(skillName) + ' (#' + escapeHtml(skillId) + ')' : 'Skill #' + escapeHtml(skillId);
  return '<a class="text-brand-300 hover:text-brand-200" href="/skills/' + encodeURIComponent(String(skillId)) + '">' + label + '</a>';
}

function renderVersionLabel(versionId: number | null | undefined, version: string | null | undefined): string | null {
  if (!versionId) return null;
  return escapeHtml('#' + versionId + (version ? ' (' + version + ')' : ''));
}

function shortCommit(value: string | null | undefined): string | null {
  const text = String(value ?? '').trim();
  if (!text) return null;
  return text.length > 12 ? text.slice(0, 12) : text;
}

function renderCertificationBadge(skill: SkillDetail): string {
  if (skill.certification_level !== 'certified' && skill.certification_level !== 'verified') return '';
  return skillCertificationBadge(skill.certification_level, 'sm');
}

function publicationLabel(skill: SkillDetail): string {
  return skill.certification_level === 'community' ? 'Community-published' : 'Registry-published';
}

function certificationLabel(skill: SkillDetail): string {
  if (skill.certification_level === 'certified') return 'Maintainer-certified';
  if (skill.certification_level === 'verified') return 'Legacy verified';
  return 'Not certified';
}

function requesterReviewLabel(skill: SkillDetail): string {
  const status = skill.governance?.requester_review?.status ?? 'not_recorded';
  if (status === 'approved') return 'Approved';
  if (status === 'changes_requested') return 'Changes requested';
  if (status === 'rejected') return 'Rejected';
  return 'Not recorded';
}

type SkillOwnerIdentity = {
  label: string;
  profileName: string | null;
};

function skillOwnerIdentity(skill: SkillDetail): SkillOwnerIdentity | null {
  const owner = subjectLabel(skill.governance?.owner_type, skill.governance?.owner_id);
  if (owner) return { label: owner, profileName: null };

  const author = String(skill.author_name || '').trim();
  if (author) return { label: author, profileName: author };

  const publisher = String(skill.published_by_name || '').trim();
  if (publisher) return { label: publisher, profileName: publisher };

  return null;
}

function renderOwnerLabel(skill: SkillDetail): string | null {
  const identity = skillOwnerIdentity(skill);
  if (!identity) return null;
  if (!identity.profileName) return escapeHtml(identity.label);
  return '<a class="text-brand-300 hover:text-brand-200" href="/users/' +
    encodeURIComponent(identity.profileName) +
    '">' +
    escapeHtml(identity.label) +
  '</a>';
}

function renderSourceIssueLink(
  skill: SkillDetail,
  sourceIssue: SkillRelatedIssuesResponse['source_issue'] | null,
): string | null {
  const id = sourceIssue?.id ?? skill.source_issue_id ?? null;
  const title = sourceIssue?.title ?? skill.source_issue_title ?? null;
  if (!id) return null;
  return '<a class="text-brand-300 hover:text-brand-200" href="/issues/' +
    encodeURIComponent(String(id)) +
    '">#' +
    escapeHtml(id) +
    (title ? ' ' + escapeHtml(title) : '') +
  '</a>';
}

function renderSourceSubmissionLink(skill: SkillDetail): string | null {
  const submissionId = skill.governance?.source_submission_id ?? null;
  if (!submissionId) return null;
  return '<a class="text-brand-300 hover:text-brand-200" href="/submissions/' +
    encodeURIComponent(String(submissionId)) +
    '">#' +
    escapeHtml(submissionId) +
  '</a>';
}

function renderCommitLabel(commitSha: string | null | undefined): string | null {
  const commit = shortCommit(commitSha);
  if (!commit) return null;
  return '<code class="rounded bg-zinc-950 px-1.5 py-0.5 text-zinc-300" title="' +
    escapeHtml(commitSha) +
    '">' +
    escapeHtml(commit) +
  '</code>';
}

function renderSourceCommitLabel(skill: SkillDetail): string | null {
  return renderCommitLabel(skill.governance?.source_commit_sha || skill.commit_sha || null);
}

function renderSourceRepoLabel(skill: SkillDetail): string | null {
  const repoId = skill.governance?.source_repo_id ?? null;
  if (!repoId) return null;
  return '<span class="text-zinc-200">Repo #' + escapeHtml(repoId) + '</span>';
}

function renderSourceSummary(
  skill: SkillDetail,
  sourceIssue: SkillRelatedIssuesResponse['source_issue'] | null,
): string | null {
  const parts = [
    { label: 'Issue', value: renderSourceIssueLink(skill, sourceIssue) },
    { label: 'Submission', value: renderSourceSubmissionLink(skill) },
    { label: 'Commit', value: renderSourceCommitLabel(skill) },
  ].filter((part): part is { label: string; value: string } => Boolean(part.value));
  return parts.length
    ? parts.map((part) => '<span class="inline-flex min-w-0 items-center gap-1"><span class="text-zinc-500">' +
      part.label + '</span>' + part.value + '</span>')
      .join('<span class="text-zinc-700" aria-hidden="true"> / </span>')
    : null;
}

function renderDerivedSourceSkillLink(skill: SkillDetail): string | null {
  const governance = skill.governance;
  if (!governance) return null;
  const sourceSkillId = governance.publication_derived_from_skill_id
    ?? (governance.publication_target_type === 'fork' ? governance.publication_target_skill_id : null);
  const sourceSkillName = sourceSkillId === governance.publication_target_skill_id
    ? governance.publication_target_skill_name
    : governance.publication_derived_from_skill_name;
  return renderSkillLink(sourceSkillId, sourceSkillName);
}

function renderVersionHistoryLink(issues: SkillRelatedIssuesResponse | null): string | null {
  const count = issues?.counts.version_issue_total ?? 0;
  if (count <= 0) return null;
  return '<a class="text-brand-300 hover:text-brand-200" href="#skill-issues-section">Version history (' +
    escapeHtml(count) +
  ')</a>';
}

function requestIssueUrl(skill: SkillDetail, issues: SkillRelatedIssuesResponse | null, intent: 'update' | 'fork'): string {
  const base = issues?.create_issue_url || ('/issues?new=1&related_skill_id=' + encodeURIComponent(String(skill.id)));
  const separator = base.includes('?') ? '&' : '?';
  return base + separator + 'request_intent=' + intent;
}

function releaseLinePolicy(skill: SkillDetail): { label: string; note: string } {
  const targetType = skill.governance?.publication_target_type ?? null;
  const sourceSkill = renderDerivedSourceSkillLink(skill);
  if (targetType === 'fork') {
    return {
      label: 'Fork / variant line',
      note: sourceSkill
        ? 'This skill has its own release line derived from a source skill; updates here stay separate from the source line.'
        : 'This skill is a variant release line; updates here stay separate from the original line.',
    };
  }
  if (targetType === 'version_update') {
    return {
      label: 'Canonical version line',
      note: 'This publication advanced an existing skill line; future updates require owner or maintainer approval.',
    };
  }
  if (targetType === 'new_skill') {
    return {
      label: 'New skill line',
      note: 'This publication started a new skill line; future updates should target this skill instead of creating silent replacements.',
    };
  }
  return {
    label: 'Published skill line',
    note: 'This skill has a published release line; update requests and variants should keep their intent explicit.',
  };
}

type PublicationFact = {
  label: string;
  valueHtml: string | null;
};

function renderPublicationFact(fact: PublicationFact): string {
  return '' +
    '<div class="min-w-0 rounded border border-zinc-800 bg-zinc-950/40 px-3 py-2">' +
      '<div class="text-[11px] font-semibold uppercase tracking-wide text-zinc-600">' + escapeHtml(fact.label) + '</div>' +
      '<div class="mt-1 min-w-0 text-sm text-zinc-200">' + (fact.valueHtml || '<span class="text-zinc-500">Not recorded</span>') + '</div>' +
    '</div>';
}

function renderPublicationFactGrid(facts: PublicationFact[]): string {
  return facts.map(renderPublicationFact).join('');
}

function renderDigestLabel(skill: SkillDetail): string {
  const digest = shortSkillDigest(skill);
  const digestFull = fullSkillDigest(skill) || 'Unavailable';
  return '<span class="inline-flex max-w-full items-center gap-1.5">' +
    '<code class="truncate rounded bg-zinc-950 px-1.5 py-0.5 text-zinc-300" title="' + escapeHtml(digestFull) + '">' + escapeHtml(digest) + '</code>' +
    (skillDigestValue(skill)
      ? '<button id="skill-digest-copy" type="button" class="btn-ghost h-7 w-7 shrink-0 p-0 text-[11px]" title="Copy content digest" aria-label="Copy content digest"><i class="fas fa-copy" aria-hidden="true"></i><span class="sr-only">Copy content digest</span></button>'
      : '') +
  '</span>';
}

function renderPublicationLineage(skill: SkillDetail): string {
  const governance = skill.governance;
  if (!governance) return '';

  const approver = subjectLabel(governance.approved_by_type, governance.approved_by_id);
  const proposedOwner = subjectLabel(governance.publication_proposed_owner_type, governance.publication_proposed_owner_id);
  const facts: PublicationFact[] = [
    { label: 'Authorized by', valueHtml: approver ? escapeHtml(approver) : null },
    { label: 'Proposed owner', valueHtml: proposedOwner ? escapeHtml(proposedOwner) : null },
    { label: 'Target skill', valueHtml: renderSkillLink(governance.publication_target_skill_id, governance.publication_target_skill_name) },
    { label: 'Target version', valueHtml: governance.publication_target_skill_current_version ? escapeHtml(governance.publication_target_skill_current_version) : null },
    { label: 'Source skill', valueHtml: renderDerivedSourceSkillLink(skill) },
    { label: 'Source version', valueHtml: renderVersionLabel(governance.publication_derived_from_version_id, governance.publication_derived_from_version) },
    { label: 'Fork reason', valueHtml: governance.publication_fork_reason ? escapeHtml(governance.publication_fork_reason) : null },
    { label: 'Compatibility', valueHtml: governance.publication_compatibility_notes ? escapeHtml(governance.publication_compatibility_notes) : null },
  ].filter((fact) => Boolean(fact.valueHtml));

  if (!facts.length) return '';
  return '' +
    '<section id="skill-publication-lineage" class="mt-4 border-t border-zinc-800 pt-4" aria-labelledby="skill-publication-lineage-title">' +
      '<h3 id="skill-publication-lineage-title" class="text-sm font-semibold text-zinc-200">Release lineage</h3>' +
      '<div class="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">' + renderPublicationFactGrid(facts) + '</div>' +
    '</section>';
}

function renderRequesterReviewProvenance(skill: SkillDetail): string {
  const review = skill.governance?.requester_review;
  if (!review?.recorded) return '';

  const actorName = review.actor_name || (review.actor_user_id ? 'user:' + review.actor_user_id : null);
  const actor = actorName && review.actor_type === 'access_token'
    ? 'Agent token: ' + actorName
    : actorName;
  const facts: PublicationFact[] = [
    { label: 'Decision', valueHtml: escapeHtml(requesterReviewLabel(skill)) },
    { label: 'Reviewer', valueHtml: actor ? escapeHtml(actor) : null },
    { label: 'Review commit', valueHtml: renderCommitLabel(review.commit_sha) },
    { label: 'Recorded at', valueHtml: review.created_at ? escapeHtml(review.created_at) : null },
    {
      label: 'Account relation',
      valueHtml: review.same_account_as_submitter == null
        ? null
        : escapeHtml(review.same_account_as_submitter ? 'Same SkillFab account' : 'Different SkillFab accounts'),
    },
  ];
  return '' +
    '<section id="skill-requester-review-provenance" class="mt-4 border-t border-zinc-800 pt-4" aria-labelledby="skill-requester-review-provenance-title">' +
      '<h3 id="skill-requester-review-provenance-title" class="text-sm font-semibold text-zinc-200">Requester review provenance</h3>' +
      '<p class="mt-1 text-xs leading-5 text-zinc-500">Explicit decision recorded for this published source commit.</p>' +
      '<div class="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">' + renderPublicationFactGrid(facts) + '</div>' +
    '</section>';
}

function renderPublicationDetailsPanel(
  skill: SkillDetail,
  files: readonly SkillFile[],
  issues: SkillRelatedIssuesResponse | null,
  sourceIssue: SkillRelatedIssuesResponse['source_issue'] | null,
): string {
  const fileCount = files.length || skill.files_count || 0;
  const owner = skillOwnerIdentity(skill)?.label || 'Owner not recorded';
  const authority = publicationAuthorityLabel(skill.governance?.approval_type);
  const releaseIntent = publicationTargetLabel(skill.governance?.publication_target_type);
  const linePolicy = releaseLinePolicy(skill);
  const sourceSubmissionId = skill.governance?.source_submission_id ?? null;
  const trustFacts: PublicationFact[] = [
    { label: 'Publication', valueHtml: escapeHtml(publicationLabel(skill)) },
    {
      label: 'Requester review',
      valueHtml: escapeHtml(requesterReviewLabel(skill)) +
        (skill.governance?.requester_review?.same_account_as_submitter
          ? '<span class="mt-1 block text-xs leading-4 text-amber-300">Same account; not independent certification.</span>'
          : ''),
    },
    { label: 'Certification', valueHtml: escapeHtml(certificationLabel(skill)) },
    { label: 'Source', valueHtml: renderSourceSummary(skill, sourceIssue) },
  ];
  const detailFacts: PublicationFact[] = [
    { label: 'Owner', valueHtml: renderOwnerLabel(skill) },
    { label: 'Current version', valueHtml: escapeHtml(skill.version || 'N/A') },
    { label: 'Publication authority', valueHtml: authority ? escapeHtml(authority) : null },
    { label: 'Release intent', valueHtml: releaseIntent ? escapeHtml(releaseIntent) : null },
    { label: 'Files', valueHtml: escapeHtml(fileCount) },
    { label: 'Digest', valueHtml: renderDigestLabel(skill) },
    { label: 'Author', valueHtml: escapeHtml(skill.author_name || skill.published_by_name || 'N/A') },
    { label: 'Source repository', valueHtml: renderSourceRepoLabel(skill) },
    { label: 'Version history', valueHtml: renderVersionHistoryLink(issues) },
  ];
  const updateUrl = requestIssueUrl(skill, issues, 'update');
  const forkUrl = requestIssueUrl(skill, issues, 'fork');

  return '' +
    '<section id="skill-publication-trust" class="card p-4" aria-labelledby="skill-publication-trust-title">' +
      '<div class="flex flex-wrap items-start justify-between gap-3">' +
        '<div>' +
          '<h2 id="skill-publication-trust-title" class="text-base font-semibold text-zinc-100">Publication &amp; trust</h2>' +
          '<p class="mt-1 text-xs leading-5 text-zinc-500">What was published, reviewed, certified, and traced to source.</p>' +
        '</div>' +
        '<span class="text-xs text-zinc-500">' + escapeHtml(owner) + '</span>' +
      '</div>' +
      '<div class="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">' + renderPublicationFactGrid(trustFacts) + '</div>' +
      '<details id="skill-publication-details" class="group mt-4 border-t border-zinc-800 pt-4">' +
        '<summary class="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-medium text-zinc-300">' +
          '<span><i class="fas fa-circle-info mr-2 text-zinc-500" aria-hidden="true"></i>Release details</span>' +
          '<i class="fas fa-chevron-down text-zinc-600 transition-transform group-open:rotate-180" aria-hidden="true"></i>' +
        '</summary>' +
        '<div id="skill-publication-overview" class="mt-4" aria-labelledby="skill-publication-overview-title">' +
          '<h3 id="skill-publication-overview-title" class="text-sm font-semibold text-zinc-200">Release provenance</h3>' +
          '<div class="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">' + renderPublicationFactGrid(detailFacts) + '</div>' +
          renderRequesterReviewProvenance(skill) +
          renderPublicationLineage(skill) +
          '<div id="skill-publication-actions" class="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-zinc-800 pt-4">' +
            '<div class="max-w-2xl">' +
              '<div class="text-sm font-semibold text-zinc-200">' + escapeHtml(linePolicy.label) + '</div>' +
              '<p class="mt-1 text-xs leading-5 text-zinc-500">' + escapeHtml(linePolicy.note) + '</p>' +
            '</div>' +
            '<div class="flex flex-wrap gap-2">' +
              (sourceSubmissionId
                ? '<a class="btn-ghost text-sm whitespace-nowrap" href="/submissions/' + encodeURIComponent(String(sourceSubmissionId)) + '"><i class="fas fa-code-commit mr-1.5"></i>Open source submission</a>'
                : '') +
              '<a class="btn-ghost text-sm whitespace-nowrap" href="' + escapeHtml(updateUrl) + '"><i class="fas fa-rotate mr-1.5"></i>Request update</a>' +
              '<a class="btn-ghost text-sm whitespace-nowrap" href="' + escapeHtml(forkUrl) + '"><i class="fas fa-code-branch mr-1.5"></i>Request fork / variant</a>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</details>' +
    '</section>';
}

export function renderStarIcons(rating: number, max: number = 5): string {
  return Array.from({ length: max }, (_, i) =>
    '<i class="fas fa-star ' + (i < Math.round(rating) ? 'text-amber-400' : 'text-zinc-700') + '"></i>'
  ).join('');
}

export function renderSkillDetailPageShell(options: SkillDetailPageShellOptions): string {
  const { skill, files, issues, feedback, certification = null, currentUserId = null, currentFileIndex } = options;
  const activeIssueCount = issues
    ? issues.counts.open_total + issues.counts.active_total
    : 0;
  const sourceIssue = issues?.source_issue || null;
  const issueLinkCount = (issues?.counts.related_total ?? 0) + (sourceIssue ? 1 : 0);

  return '' +
    '<div class="card p-5">' +
      '<div class="flex flex-wrap items-start justify-between gap-3">' +
        '<div class="min-w-0">' +
          '<h1 class="text-2xl font-bold text-white inline-flex items-center gap-3">' + escapeHtml(skill.name || 'Unnamed skill') + ' ' +
            renderCertificationBadge(skill) +
          '</h1>' +
          '<p class="mt-2 max-w-4xl text-sm leading-6 text-zinc-400">' + escapeHtml(skill.description || 'No description') + '</p>' +
        '</div>' +
        '<div class="flex gap-2 text-xs">' +
          '<button id="star-btn" onclick="window.__openRatingPanel()" ' +
            'class="btn-ghost text-sm flex items-center gap-1.5" style="display:none" aria-label="Rate this skill">' +
            '<i class="fas fa-star" id="star-icon" aria-hidden="true"></i>' +
            '<span id="star-count">0</span>' +
          '</button>' +
          '<a class="btn-ghost" href="/skills">Back to list</a>' +
        '</div>' +
      '</div>' +
    '</div>' +
    '<div id="skill-primary-experience" class="grid grid-cols-1 gap-4 lg:grid-cols-3">' +
      '<aside class="order-1 space-y-4 lg:order-2">' +
        renderUsePanel(skill) +
      '</aside>' +
      '<div class="order-2 min-w-0 lg:order-1 lg:col-span-2">' +
        renderSkillInstructionsPanel(skill) +
      '</div>' +
    '</div>' +
    renderPublicationDetailsPanel(skill, files, issues, sourceIssue) +
    renderCertificationRequestPanel(skill, certification, currentUserId) +
    renderPackageReviewFilesSection({
      surface: 'published-skill',
      files,
      currentIndex: currentFileIndex,
      containerId: 'skill-file-explorer',
      selectFile: 'selectFile',
      copyFileContent: 'copyFileContent',
    }) +
    options.renderFlywheelSummary({
      download_count: skill.download_count,
      feedback_count: feedback?.feedback_count ?? skill.feedback_count ?? 0,
      related_issue_count: issueLinkCount,
      active_issue_count: activeIssueCount,
      version: skill.version,
      source_issue_id: sourceIssue?.id ?? skill.source_issue_id ?? null,
      source_issue_title: sourceIssue?.title ?? skill.source_issue_title ?? null,
    }) +
    options.renderRelatedIssuesSection(issues) +
    '<section id="skill-analytics-section"><div class="card p-5"><p class="text-zinc-500 text-sm">Loading analytics...</p></div></section>' +
    '<div id="reviews-section" class="card p-5">' +
      '<h2 class="text-base font-semibold text-zinc-200 mb-4">' +
        '<i class="fas fa-star text-amber-400 mr-2"></i>Ratings &amp; Reviews' +
      '</h2>' +
      '<div id="rating-summary" class="text-zinc-500 text-sm mb-5">Loading...</div>' +
      '<div id="review-form-container"></div>' +
      '<div id="reviews-list" class="space-y-3 mt-4">' +
        '<p class="text-zinc-500 text-sm">Loading reviews...</p>' +
      '</div>' +
    '</div>';
}
